import { useEffect, useRef } from "react";
import { toPng } from "html-to-image";
import html2canvas from "html2canvas";
import { usePosterContext } from "../../../../context/poster-context";
import {
  Preview1Background,
  Preview1Wrapper,
  Preview1ContentLeft,
  Preview1ContentRight,
  Preview1ContentWrapper,
  Preview1Header,
  Preview1HeaderBottom,
  Preview1HeaderLeft,
  Preview1HeaderPubName,
  Preview1HeaderStartDate,
  Preview1HeaderSubTitle,
  Preview1HeaderTitle,
  Preview1InfoHeader,
  Preview1InfoItem,
  Preview1InfoItemWrapper,
  Preview1InfoLine,
  Preview1InfoWrapper,
  Preview1NoticeWrapper,
  Preview1NoticeTitle,
  Preview1NoticeValue,
  Preview1PrizePoolWrapper,
  Preview1PrizePoolTitle,
  Preview1PrizePoolListWrapper,
  Preview1PrizePoolItem,
  Preview1StructureTitle,
  Preview1StructureListWrapper,
  Preview1StructureHeader,
  Preview1StructureHeaderItem,
  Preview1StructureItem,
  Preview1StructureRow,
  Preview1StructureBreak,
  Preview1HeaderAll,
  PreviewImageWrapper,
  Preview1AddressWrapper,
} from "./styles/preview-1.style";
import { PosterInfoType, PosterTab } from "../../../../hooks/use-poster";

const Preview1 = () => {
  const {
    pubName,
    startDate,
    selectedTournament,
    title,
    subTitle,
    // info tab
    infoSection,
    infoFontSize,
    tournamentType,
    registerLimitLevel,
    entry,
    buyIn,
    startingStack,
    reBuyIn,
    reBuyInStack,
    addOn,
    addOnStack,
    duration,
    // address tab
    showAddress,
    address,
    // notice tab
    noticeSection,
    notice,
    // prize pool tab
    prizePoolSection,
    prizePool,
    prizePoolFontSize,
    // structure tab
    structureSection,
    blindStructure,
    showLevel,
    showSmallBlind,
    showBigBlind,
    showAnte,
    showDuration,
    levelLabel,
    smallBlindLabel,
    bigBlindLabel,
    anteLabel,
    durationLabel,
    //util
    isHideLine,
    infoType,
    setInfoType,
    setIsHideLine,
    setTab,
    breakText,
    breakTime,
    // image tab
    image1,
    image2,
    topPadding,
    rightPadding,
  } = usePosterContext();
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const downloadImage = async () => {
      if (!previewRef.current) return;
      const isHide = isHideLine;

      setIsHideLine(true);

      try {
        // 이미지를 base64로 변환하여 CORS 문제 완전히 회피
        const convertImagesToBase64 = async (): Promise<void> => {
          if (!previewRef.current) return;

          const images = previewRef.current.querySelectorAll("img");
          const conversionPromises: Promise<void>[] = [];

          images.forEach((img) => {
            // blob URL이나 data URL이 아닌 경우만 변환
            if (
              img.src &&
              !img.src.startsWith("blob:") &&
              !img.src.startsWith("data:")
            ) {
              return;
            }

            const promise = new Promise<void>((resolve) => {
              const image = new Image();
              image.crossOrigin = "anonymous";

              image.onload = () => {
                try {
                  const canvas = document.createElement("canvas");
                  canvas.width = image.width;
                  canvas.height = image.height;
                  const ctx = canvas.getContext("2d");
                  if (ctx) {
                    ctx.drawImage(image, 0, 0);
                    const base64 = canvas.toDataURL("image/png");
                    img.src = base64;
                  }
                } catch (e) {
                  console.warn("이미지 변환 실패:", e);
                }
                resolve();
              };

              image.onerror = () => {
                resolve(); // 에러가 나도 진행
              };

              image.src = img.src;
            });

            conversionPromises.push(promise);
          });

          await Promise.all(conversionPromises);
          // 변환 완료 후 렌더링을 위한 대기
          await new Promise((resolve) => setTimeout(resolve, 300));
        };

        await convertImagesToBase64();

        // html-to-image 사용 (box-shadow 지원이 더 좋음)
        let dataUrl: string;

        try {
          dataUrl = await toPng(previewRef.current, {
            cacheBust: true,
            pixelRatio: 1.8,
            backgroundColor: "#ffffff",
            style: {
              transform: "none",
              position: "static",
            },
          });
        } catch (htmlToImageError) {
          // html-to-image 실패 시 html2canvas로 fallback
          console.log(
            "html-to-image 실패, html2canvas 사용:",
            htmlToImageError
          );
          const canvas = await html2canvas(previewRef.current, {
            backgroundColor: "#ffffff",
            scale: 1.8,
            useCORS: true,
            allowTaint: false,
            logging: false,
          });

          // 캔버스를 원하는 크기로 조정
          const resizedCanvas = document.createElement("canvas");
          resizedCanvas.width = 1116;
          resizedCanvas.height = 1576;
          const ctx = resizedCanvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(canvas, 0, 0, 1116, 1576);
            dataUrl = resizedCanvas.toDataURL("image/png");
          } else {
            dataUrl = canvas.toDataURL("image/png");
          }
        }

        const fileName = selectedTournament?.title || "poster";

        // 이미지 저장
        // File System Access API 지원 여부 확인
        if ("showSaveFilePicker" in window) {
          try {
            // dataUrl을 Blob으로 변환
            const response = await fetch(dataUrl);
            const blob = await response.blob();

            // 파일 저장 다이얼로그 표시
            const fileHandle = await (window as any).showSaveFilePicker({
              suggestedName: `${fileName}_preview.png`,
              types: [
                {
                  description: "PNG Image",
                  accept: {
                    "image/png": [".png"],
                  },
                },
              ],
            });

            // 파일 쓰기
            const writable = await fileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
          } catch (saveErr: any) {
            // 사용자가 취소한 경우는 에러로 처리하지 않음
            if (saveErr.name !== "AbortError") {
              console.error("파일 저장 실패:", saveErr);
              // File System Access API 실패 시 기존 방식으로 fallback
              const link = document.createElement("a");
              link.download = `${fileName}_preview.png`;
              link.href = dataUrl;
              link.click();
            }
          }
        } else {
          // File System Access API를 지원하지 않는 브라우저는 기존 방식 사용
          const link = document.createElement("a");
          link.download = `${fileName}_preview.png`;
          link.href = dataUrl;
          link.click();
        }
      } catch (err) {
        console.error("이미지 생성 실패:", err);
        alert("이미지 생성에 실패했습니다.");
      }
      setIsHideLine(isHide);
    };

    // 다운로드 버튼 클릭 이벤트 리스너
    const handleDownloadClick = () => {
      downloadImage();
    };

    window.addEventListener("download-poster-image", handleDownloadClick);

    return () => {
      window.removeEventListener("download-poster-image", handleDownloadClick);
    };
  }, [selectedTournament, isHideLine, setIsHideLine]);

  return (
    <Preview1Wrapper ref={previewRef}>
      <Preview1Background
        src="/image-web/poster/poster1/poster1_back.png"
        alt="poster1_back_top"
      />
      <Preview1HeaderAll
        isHideLine={isHideLine}
        infoType={infoType === PosterInfoType.MAIN}
        onClick={() => {
          setInfoType(PosterInfoType.MAIN);
          setTab(PosterTab.INFO);
        }}
      >
        <Preview1Header>
          <Preview1HeaderLeft>
            <Preview1HeaderPubName
              color={pubName.color || ""}
              isShow={pubName.isShow}
            >
              {pubName.isShow ? pubName.value : ""}
            </Preview1HeaderPubName>
            <Preview1HeaderStartDate color={startDate.color || ""}>
              {startDate.isShow ? startDate.value : ""}
            </Preview1HeaderStartDate>
          </Preview1HeaderLeft>
          {!image1 && !image2 ? (
            <img
              src="/image-web/poster/poster1/poster1_logo.png"
              alt="poster1_logo"
            />
          ) : (
            <PreviewImageWrapper
              topPadding={topPadding}
              rightPadding={rightPadding}
            >
              {image2 && <img src={image2} alt="image2" />}
              {image1 && <img src={image1} alt="image1" />}
            </PreviewImageWrapper>
          )}
        </Preview1Header>
        <Preview1HeaderBottom>
          <Preview1HeaderSubTitle color={subTitle.color || ""}>
            {subTitle.isShow ? subTitle.value : ""}
          </Preview1HeaderSubTitle>
          <Preview1HeaderTitle color={title.color || ""}>
            {title.isShow ? title.value : ""}
          </Preview1HeaderTitle>
        </Preview1HeaderBottom>
      </Preview1HeaderAll>

      <Preview1ContentWrapper>
        <Preview1ContentLeft>
          <Preview1InfoWrapper
            isHideLine={isHideLine}
            infoType={infoType === PosterInfoType.INFO}
            onClick={() => {
              setInfoType(PosterInfoType.INFO);
              setTab(PosterTab.INFO);
            }}
          >
            <Preview1InfoHeader>{infoSection}</Preview1InfoHeader>
            <Preview1InfoLine />
            <Preview1InfoItemWrapper fontSize={infoFontSize}>
              <Preview1InfoItem isShow={tournamentType.isShow}>
                <div>{tournamentType.label}</div>
                <div>{tournamentType.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={registerLimitLevel.isShow}>
                <div>{registerLimitLevel.label}</div>
                <div>{registerLimitLevel.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={entry.isShow}>
                <div>{entry.label}</div>
                <div>{entry.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={buyIn.isShow}>
                <div>{buyIn.label}</div>
                <div>{buyIn.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={startingStack.isShow}>
                <div>{startingStack.label}</div>
                <div>{startingStack.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={reBuyIn.isShow}>
                <div>{reBuyIn.label}</div>
                <div>{reBuyIn.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={reBuyInStack.isShow}>
                <div>{reBuyInStack.label}</div>
                <div>{reBuyInStack.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={addOn.isShow}>
                <div>{addOn.label}</div>
                <div>{addOn.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={addOnStack.isShow}>
                <div>{addOnStack.label}</div>
                <div>{addOnStack.value}</div>
              </Preview1InfoItem>
              <Preview1InfoItem isShow={duration.isShow}>
                <div>{duration.label}</div>
                <div>{duration.value}</div>
              </Preview1InfoItem>
            </Preview1InfoItemWrapper>
          </Preview1InfoWrapper>
          <Preview1NoticeWrapper
            isHideLine={isHideLine}
            infoType={infoType === PosterInfoType.NOTICE}
            onClick={() => {
              setInfoType(PosterInfoType.NOTICE);
              setTab(PosterTab.INFO);
            }}
          >
            <Preview1NoticeTitle>{noticeSection}</Preview1NoticeTitle>
            <Preview1NoticeValue dangerouslySetInnerHTML={{ __html: notice }} />
          </Preview1NoticeWrapper>
          <Preview1PrizePoolWrapper
            isHideLine={isHideLine}
            showAddress={showAddress}
            infoType={infoType === PosterInfoType.PRIZE_POOL}
            onClick={() => {
              setInfoType(PosterInfoType.PRIZE_POOL);
              setTab(PosterTab.INFO);
            }}
          >
            <Preview1PrizePoolTitle>{prizePoolSection}</Preview1PrizePoolTitle>
            <Preview1PrizePoolListWrapper
              fontSize={prizePoolFontSize}
              showAddress={showAddress}
            >
              {prizePool.map((item, index) => (
                <Preview1PrizePoolItem key={`prize-pool-${index}`}>
                  <div>{item.rank}</div>
                  <div>{item.prize}</div>
                </Preview1PrizePoolItem>
              ))}
            </Preview1PrizePoolListWrapper>
          </Preview1PrizePoolWrapper>
        </Preview1ContentLeft>
        <Preview1ContentRight
          isHideLine={isHideLine}
          infoType={infoType === PosterInfoType.STRUCTURE}
          onClick={() => {
            setInfoType(PosterInfoType.STRUCTURE);
            setTab(PosterTab.INFO);
          }}
          showAddress={showAddress}
        >
          <Preview1StructureTitle>{structureSection}</Preview1StructureTitle>
          <Preview1StructureListWrapper showAddress={showAddress}>
            <Preview1StructureHeader>
              {showLevel && (
                <Preview1StructureHeaderItem flex={1}>
                  {levelLabel}
                </Preview1StructureHeaderItem>
              )}
              {showSmallBlind && (
                <Preview1StructureHeaderItem flex={2}>
                  {smallBlindLabel}
                </Preview1StructureHeaderItem>
              )}
              {showBigBlind && (
                <Preview1StructureHeaderItem flex={2}>
                  {bigBlindLabel}
                </Preview1StructureHeaderItem>
              )}
              {showAnte && (
                <Preview1StructureHeaderItem flex={2}>
                  {anteLabel}
                </Preview1StructureHeaderItem>
              )}
              {showDuration && (
                <Preview1StructureHeaderItem flex={1}>
                  {durationLabel}
                </Preview1StructureHeaderItem>
              )}
            </Preview1StructureHeader>
            {blindStructure.map((item, index) => {
              if (item.type === "BREAK") {
                return (
                  <Preview1StructureBreak key={`break-${index}`}>
                    {breakText} {item.time} {breakTime}
                  </Preview1StructureBreak>
                );
              }
              return (
                <Preview1StructureRow key={item.level || `row-${index}`}>
                  {showLevel && (
                    <Preview1StructureItem flex={1} isShow={showLevel}>
                      {item.level}
                    </Preview1StructureItem>
                  )}
                  {showSmallBlind && (
                    <Preview1StructureItem flex={2} isShow={showSmallBlind}>
                      {item.smallBlind?.toLocaleString()}
                    </Preview1StructureItem>
                  )}
                  {showBigBlind && (
                    <Preview1StructureItem flex={2} isShow={showBigBlind}>
                      {item.bigBlind?.toLocaleString()}
                    </Preview1StructureItem>
                  )}
                  {showAnte && (
                    <Preview1StructureItem flex={2} isShow={showAnte}>
                      {item.ante?.toLocaleString()}
                    </Preview1StructureItem>
                  )}
                  {showDuration && (
                    <Preview1StructureItem flex={1} isShow={showDuration}>
                      {item.time}
                    </Preview1StructureItem>
                  )}
                </Preview1StructureRow>
              );
            })}
          </Preview1StructureListWrapper>
        </Preview1ContentRight>
      </Preview1ContentWrapper>
      {showAddress && (
        <Preview1AddressWrapper>{address}</Preview1AddressWrapper>
      )}
    </Preview1Wrapper>
  );
};

export default Preview1;
